<template>
  <div v-if="!loading">
    <FlowHeading
      title="Select your business entity type"
      title-type="title1"
      sub-title="Letting us know about your business entity setup is important to guide you and your clients
      in creating the correct contract types, so you don't need to worry about compliance."
    />
    <BusinessEntityTypeInput market="CA">
      <div class="tw-mt-4">
        <WdsRadioGroup v-model:checked="hasBusinessEntity" name="has-business-entity">
          <WdsRadio :disabled="!canChange" :value="true">I have a business entity setup</WdsRadio>
          <WdsRadio :disabled="!canChange" :value="false">
            I do not have a business entity setup
          </WdsRadio>
        </WdsRadioGroup>
      </div>
    </BusinessEntityTypeInput>
    <div class="wds-input-group continue-button">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { WdsButton, WdsRadio, WdsRadioGroup } from '@worksome/wds-components'
import gql from 'graphql-tag'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import BusinessEntityTypeInput from '@/components/compliances/BusinessEntity/BusinessEntityTypeInput.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'

export default {
  name: 'CaBusinessEntityType',

  components: {
    BusinessEntityTypeInput,
    ButtonSpinner,
    FlowHeading,
    WdsButton,
    WdsRadio,
    WdsRadioGroup,
  },

  inject: ['skip', 'next', 'refreshFlowCoordinates'],

  data() {
    return {
      loading: true,
      isSubmitting: false,
      hasBusinessEntity: undefined,
      compliance: undefined,
      complianceData: undefined,
    }
  },
  computed: {
    formIsValid() {
      return this.hasBusinessEntity !== undefined
    },
    canChange() {
      return this.complianceData?.canChange
    },
    lacksRequiredFields() {
      return !this.formIsValid || !this.canChange
    },
    canSubmit() {
      return !this.isSubmitting && this.formIsValid
    },
  },

  apollo: {
    me: {
      query: gql`
        query me {
          me {
            activeAccount {
              compliances(names: [FREELANCER_CA_BUSINESS_ENTITY]) {
                name
                isApplicable
                isComplete
                data
              }
            }
          }
        }
      `,
      result({ data }) {
        this.compliance = data.me?.activeAccount?.compliances?.[0]

        this.complianceData = JSON.parse(this?.compliance?.data ?? '{}')

        this.hasBusinessEntity =
          this.complianceData.complianceInput?.data?.has_ca_business_entity ?? undefined

        this.loading = false
      },
      update: (data) => data.me,
    },
  },
  watch: {
    async hasBusinessEntity() {
      this.isSubmitting = true
      await this.saveSelection()
      await this.refreshFlowCoordinates()
      this.isSubmitting = false
    },
  },
  methods: {
    async callNextScreen() {
      this.isSubmitting = true
      await this.saveSelection()
      this.next()
    },

    async saveSelection() {
      const data = JSON.stringify({
        ...this.complianceData.complianceInput?.data,
        has_ca_business_entity: this.hasBusinessEntity,
      })

      await this.$apollo.mutate({
        mutation: UpdateComplianceMutation,
        variables: {
          input: {
            name: 'FREELANCER_CA_BUSINESS_ENTITY',
            data,
          },
        },
        context: {
          headers: {
            'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.continue-button {
  margin-top: 32px;
}
.text-tooltip {
  margin-bottom: 16px;
}
.button-ghost-container {
  margin-top: 16px;
}
</style>
